import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useModal } from '../../../contexts/ModalContext';

const ConsultationProcess = () => {
  const { openConsultation } = useModal();
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      id: 'discovery',
      title: 'Discovery Call',
      duration: '30 minutes',
      description: 'We start with a friendly conversation to understand your business goals, challenges, and vision.',
      details: [
        'Understand your business objectives and target audience',
        'Discuss current challenges and pain points',
        'Explore your vision for the digital solution',
        'Identify key success metrics and KPIs',
        'Review any existing systems or constraints'
      ],
      icon: 'MessageSquare',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'analysis',
      title: 'Technical Analysis',
      duration: '2-3 days',
      description: 'Our team conducts a thorough analysis of your requirements and creates a detailed technical roadmap.',
      details: [
        'Technical feasibility assessment',
        'Architecture planning and system design',
        'Technology stack recommendations',
        'Integration requirements analysis',
        'Security and scalability considerations'
      ],
      icon: 'Search',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      id: 'proposal',
      title: 'Detailed Proposal',
      duration: '1-2 days',
      description: 'We present a comprehensive proposal with timeline, pricing, and project milestones.',
      details: [
        'Detailed project scope and deliverables',
        'Phase-wise timeline with milestones',
        'Transparent pricing breakdown',
        'Team composition and roles',
        'Risk assessment and mitigation strategies'
      ],
      icon: 'FileText',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'kickoff',
      title: 'Project Kickoff',
      duration: '1 day',
      description: 'Once approved, we begin with project setup, team introductions, and initial planning.',
      details: [
        'Team introductions and role clarifications',
        'Project management tool setup',
        'Communication channels establishment',
        'Initial wireframes and mockups review',
        'Development environment preparation'
      ],
      icon: 'Rocket',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  const faqs = [
    {
      question: 'How long does a project take?',
      answer: 'Simple marketing websites and landing pages are typically delivered within 1 to 3 weeks. Complex custom software applications, admin dashboards, or mobile apps can take between 4 to 12 weeks. We discuss exact milestones during our planning phase.'
    },
    {
      question: 'Do you develop custom software?',
      answer: 'Yes, we build custom business software from scratch, including inventory trackers, custom billing systems, booking planners, and lightweight CRM or ERP modules. Everything is built to fit your operational workflows.'
    },
    {
      question: 'Can you improve an existing website?',
      answer: 'Absolutely. We can take your existing website and improve its loading speed, fix mobile layout bugs, add secure features, optimize it for search engines, or perform a complete content rewrite.'
    },
    {
      question: 'Do you provide maintenance?',
      answer: 'Yes, we offer ongoing technical support and maintenance plans. This includes monitoring uptime, applying security patches, taking regular database backups, and allocating hours for minor text/image updates.'
    },
    {
      question: 'Can you integrate AI?',
      answer: 'Yes. We specialize in AI integrations like automated WhatsApp notifications, customer support chatbots using OpenAI/Groq APIs, and automated lead routing systems.'
    },
    {
      question: 'Do you develop mobile apps?',
      answer: 'Yes. We use cross-platform frameworks like React Native and Flutter to deploy native-performance mobile apps for both iOS and Android, which reduces development time and cost.'
    },
    {
      question: 'Can you redesign our website?',
      answer: 'Yes. We design interactive UI prototypes in Figma first, allowing you to preview, click through, and request adjustments before we begin writing any code.'
    },
    {
      question: 'Do you provide hosting?',
      answer: 'Yes. We configure and manage secure cloud deployments on platforms like AWS, Vercel, and Netlify, including SSL setups, custom domain connections, and automated scaling.'
    },
    {
      question: 'Do you support startups?',
      answer: 'Definitely. We enjoy collaborating with fellow startups. We offer flexible phase-by-phase development packages, transparent payment terms, and direct technical consultations to help you launch fast.'
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="space-y-12">
      {/* Process Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h2 className="text-2xl font-bold text-foreground mb-4">Our Consultation Process</h2>
        <p className="text-text-secondary max-w-2xl mx-auto">
          We've designed a transparent, collaborative process that ensures your project starts on the right foot. Here's exactly what to expect when you work with us.
        </p>
      </motion.div>

      {/* Process Steps */}
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute left-8 top-16 bottom-16 w-0.5 bg-border hidden md:block" />
        <div
          className="absolute left-8 top-16 w-0.5 bg-gradient-to-b from-primary to-accent transition-all duration-1000 hidden md:block"
          style={{ height: `${(activeStep + 1) * 25}%` }}
        />

        <div className="space-y-8">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`relative cursor-pointer ${activeStep === index ? 'scale-105' : ''
                } transition-transform duration-300`}
              onClick={() => setActiveStep(index)}
            >
              <div className="flex items-start space-x-6">
                {/* Step Icon */}
                <div className={`relative z-10 w-16 h-16 ${step.bgColor} rounded-full flex items-center justify-center border-4 border-background shadow-lg ${activeStep === index ? 'ring-4 ring-primary/20' : ''
                  }`}>
                  <Icon name={step.icon} size={24} className={step.color} />
                  {activeStep > index && (
                    <div className="absolute inset-0 bg-success/20 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={16} color="var(--color-success)" />
                    </div>
                  )}
                </div>

                {/* Step Content */}
                <div className="flex-1 card p-6 hover:shadow-brand-lg transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xl font-semibold text-foreground">{step.title}</h4>
                    <span className="text-sm text-text-secondary bg-muted px-3 py-1 rounded-full">
                      {step.duration}
                    </span>
                  </div>

                  <p className="text-text-secondary mb-4">{step.description}</p>

                  <AnimatePresence>
                    {activeStep === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-border pt-4"
                      >
                        <h5 className="font-medium text-foreground mb-3">What we'll cover:</h5>
                        <ul className="space-y-2">
                          {step.details.map((detail, detailIndex) => (
                            <motion.li
                              key={detailIndex}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: detailIndex * 0.1 }}
                              className="flex items-start space-x-2 text-sm text-text-secondary"
                            >
                              <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                              <span>{detail}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-8 text-center border border-primary/20"
      >
        <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Calendar" size={32} color="white" />
        </div>
        <h4 className="text-2xl font-bold text-foreground mb-2">Ready to Get Started?</h4>
        <p className="text-text-secondary mb-6 max-w-md mx-auto">
          Book your free consultation today and let's discuss how we can bring your vision to life.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="default"
            size="lg"
            iconName="Calendar"
            iconPosition="left"
            className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand"
            onClick={openConsultation}
          >
            Schedule Free Consultation
          </Button>
          <Button
            variant="outline"
            size="lg"
            iconName="MessageCircle"
            iconPosition="left"
            className="border-primary/20 text-primary hover:bg-primary/5"
            onClick={() => window.open('https://wa.me/919003241548', '_blank')}
          >
            Chat with Our Team
          </Button>
        </div>
      </motion.div>

      {/* FAQ Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1 }}
      >
        <h4 className="text-xl font-semibold text-foreground mb-6 text-center">Frequently Asked Questions</h4>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.1 + index * 0.1 }}
              className="card border border-border hover:border-primary/30 transition-all duration-300"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full p-6 text-left flex items-center justify-between"
              >
                <span className="font-medium text-foreground pr-4">{faq.question}</span>
                <Icon
                  name={openFaq === index ? "ChevronUp" : "ChevronDown"}
                  size={20}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              <AnimatePresence>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-6"
                  >
                    <div className="border-t border-border pt-4">
                      <p className="text-text-secondary">{faq.answer}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default ConsultationProcess;